import {
	type accessCredentialsVerificationIdType,
	handleApiError,
	type IUseAccessCredentials,
	type IUseAccessCredentialsArg,
} from 'BreetHelpers';
import {
	closeModal,
	consumeAccessCredentialEvent,
	openModal,
	selectAccessCredentialEvents,
	useAppDispatch,
	useAppSelector,
} from 'BreetRedux';
import { useCallback, useEffect, useState } from 'react';

export const useAccessCredentials = ({
	mode,
	submitRequest,
	id,
	type,
	description = '',
	title = '',
	dialogMessage,
	closeModalId,
	persistOnVerify,
	render,
	inputLength,
	generateOTP = true,
	refetchFn,
	resendOtp,
	buttonTitle,
}: IUseAccessCredentialsArg): IUseAccessCredentials => {
	const [selectedId, setSelectedId] = useState<accessCredentialsVerificationIdType>('');
	const [fieldError, setFieldError] = useState<string | undefined>();
	const accessCredentialEvents = useAppSelector(selectAccessCredentialEvents);

	const dispatch = useAppDispatch();

	const clearError = useCallback(() => {
		setFieldError(undefined);
	}, []);

	const handleVerification = useCallback(() => {
		setSelectedId(id);
		if (!render) {
			dispatch(
				openModal({
					id: 'accessCredentials',
					metadata: { mode, type, verificationId: id, title, description, inputLength, generateOTP, resendOtp, buttonTitle },
				})
			);
		}
	}, [dispatch, id, description, title, mode, type, render, inputLength, generateOTP, resendOtp, buttonTitle]);

	const onSuccess = useCallback(() => {
		setFieldError(undefined);
		dispatch(closeModal('loadingDialog'));

		if (!persistOnVerify) {
			dispatch(closeModal('accessCredentials'));
		}

		if (closeModalId !== undefined) {
			dispatch(closeModal(closeModalId));
		}
	}, [closeModalId, dispatch, persistOnVerify]);

	const onError = useCallback(
		(error?: unknown) => {
			dispatch(closeModal('loadingDialog'));
			if (error) {
				setFieldError((error as { data?: { message?: string } }).data?.message ?? 'Invalid code, please try again');
				handleApiError(error);
			}
		},
		[dispatch]
	);

	const dispatchAccessCredentials = useCallback(
		(code?: string) => {
			if (code) {
				setFieldError(undefined);

				if (dialogMessage) {
					dispatch(openModal({ id: 'loadingDialog', dialogMessage }));
				}

				submitRequest({ code, onSuccess, onError })?.catch(console.error);
			}
		},
		[dialogMessage, submitRequest, onSuccess, onError, dispatch]
	);

	useEffect(() => {
		const handleRequest = async () => {
			const event = accessCredentialEvents.find((e) => e.verificationId === selectedId);

			if (event) {
				dispatch(consumeAccessCredentialEvent(event.verificationId));
				setFieldError(undefined);

				if (dialogMessage) {
					dispatch(openModal({ id: 'loadingDialog', dialogMessage }));
				}

				await submitRequest({ onSuccess, onError, code: event.code });
			}
		};

		handleRequest().catch(console.error);
	}, [accessCredentialEvents, dialogMessage, dispatch, onError, onSuccess, selectedId, submitRequest]);

	const component = render?.({
		mode,
		type,
		title,
		description,
		dispatchAccessCredentials,
		inputLength,
		generateOTP,
		refetchFn,
		resendOtp,
		buttonTitle,
		error: fieldError,
		clearError,
	});

	return {
		handleVerification,
		onSuccess,
		onError,
		submitRequest,
		mode,
		description,
		title,
		type,
		component,
		buttonTitle,
	};
};
